import React from "react";
import { Document, Image, Page, Text, View } from "@react-pdf/renderer";
import TimeCalculator, { UpDownTimeOnEachLeg } from "./TimeCalculator";
import { Aircraft, Company, Person, Signable, TicketLineItem, TicketLineItemType, TicketLocation } from "./avroGraphql";
import transparentPixel from "./transparent-pixel.png";
import logo from "./bailey-helicopters-logo-monochrome.png";
import dayjs from "dayjs";
import {
    allHighlights,
    calculateColumnWidths,
    createGridColumnStyles,
    gridHeaderWithColumnLines,
    highlightEvenRows,
    highlightOddRows,
    highlightOddRowsAndColumnLines,
    noHighlights,
    PAGE_WIDTH,
    renderGrid,
    renderSectionHeader,
    STANDARD_LINE,
    styles,
    VERTICAL_GAP,
} from "./ReportBuilder";
import { DefinitelyAnArray, GetLineItemPrecision, getTicketNumber, HasValue, IsDemoMode, sortFlightLegsByTime, sortFlightLegsManually } from "./HelperMethods";
import {
    APPLICATION_VERSION,
    CHARGE_LINE_ITEM_TYPES,
    FUEL_BASE_OPERATOR,
    FUEL_REMOTE_CUSTOMER,
    FUEL_REMOTE_OPERATOR,
    LANDING_LINE_ITEM_TYPES,
    MIN_HELI,
    MIN_PILOT,
    MINIMUM_LINE_ITEM_TYPES,
} from "./metadata";
import { ErrorLevel, ValidationResult } from "../Utils/Interfaces";
import { JobExtended, TicketExtended } from "../Utils/TypeExtensions";
import { getDescriptiveName } from "../Utils/TicketErrorHelper";

// react-pdf default units are pt (1/72")
// using 1/4" margins (18pt) on letter (8.5") size paper for 8" (576pt) of usable width
const SIGNATURE_WIDTH = 216;
const SIGNATURE_HEIGHT = 64.5;
export const SIGNATURE_ASPECT_RATIO = SIGNATURE_WIDTH / SIGNATURE_HEIGHT;

// NOTE - minimumsRequiredList doesn't come from GQL yet but will soon - use MINIMUMS_VISIBLE_COMPANIES from metadata.tsx in the meantime
interface TicketPdfProps {
    ticket: TicketExtended;
    job: JobExtended;
    aircraftList: Aircraft[];
    companyList: Company[];
    lineItemTypeList: TicketLineItemType[];
    minimumsRequiredList: number[];
    personList: Person[];
    validationResults: ValidationResult[];
    breadcrumb: string;
    showDataDisclaimer: boolean;
    isSuperUser: boolean;
}

export const TripTicketJsonPdf = (props: TicketPdfProps): JSX.Element => {
    function getAircraft(aircraftId: number) {
        return props.aircraftList.find((a) => a.id === aircraftId);
    }

    function getCompanyName(companyId: number) {
        const company = props.companyList.find((c) => c.id === companyId);
        return `${company?.name}`.toLocaleUpperCase();
    }

    function getLineItemTypeLabel(lineItemTypeId: number) {
        const lineItemType = props.lineItemTypeList.find((t) => t.id === lineItemTypeId);
        return `${lineItemType?.label}`.toLocaleUpperCase();
    }

    function isMinimumsRequired(companyId: number) {
        const index = props.minimumsRequiredList.findIndex((c) => {
            return c === companyId;
        });

        return index >= 0;
    }

    function getPersonName(personId: number) {
        const person = props.personList.find((p) => p.id === personId);
        return `${person?.first_name} ${person?.last_name}`.toLocaleUpperCase();
    }

    function createFormHeaderSection() {
        const ticketNumber = getTicketNumber(props.ticket);
        return (
            <View style={{ flexDirection: "row" }}>
                <View style={{ width: 140 }}>
                    <Image style={{ width: 72 }} src={logo} />
                </View>
                <View style={{ flex: 1, width: 262 }}>
                    <Text style={styles.corporateAddress}>BAILEY HELICOPTERS LTD.</Text>
                    <Text style={styles.corporateAddress}>600 PALMER ROAD NE, CALGARY, AB, T2E 7R3</Text>
                    <Text style={styles.corporateAddress}>TEL (403) 219-2770 FAX (403) 219-2775</Text>
                    <Text style={styles.corporateAddress}>EMAIL: accountsreceivable@baileyhelicopters.com</Text>
                </View>
                <View style={{ width: 182, flexDirection: "column", marginTop: 10 }}>
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <Text style={{ ...styles.ticketNumberLabel, width: 80 }}>FLIGHT TICKET NO :</Text>
                        <Text style={{ ...styles.ticketNumber, width: 94 }}>{ticketNumber}</Text>
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <Text style={{ ...styles.ticketNumberLabel, width: 80, paddingTop: 1 }}>DATE :</Text>
                        <Text
                            style={{
                                ...styles.ticketNumber,
                                width: 94,
                                paddingTop: 1,
                            }}>
                            {dayjs(props.ticket.date).format("MM/DD/YYYY")}
                        </Text>
                    </View>
                </View>
            </View>
        );
    }

    const createNotesSection = (width: number): JSX.Element => {
        if (!HasValue(props.ticket.comments)) return <></>;

        const sectionKey = "notes";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "NOTES")}
                {createNotesTextArea(width)}
            </View>
        );
    };

    const createNotesTextArea = (width: number): JSX.Element => {
        const noteStyle = { ...styles.gridCell, width: width };

        return (
            <View key={"notesn"} style={{ flexDirection: "row" }} wrap={false}>
                <Text key={"notesnn"} style={noteStyle}>
                    {props.ticket.comments}
                </Text>
            </View>
        );
    };

    const createStampSection = (width: number): JSX.Element => {
        const sectionKey = "stamp";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "CUSTOMER STAMP")}
                {createStampBox(width)}
            </View>
        );
    };

    const createStampBox = (width: number): JSX.Element => {
        const noteStyle = { ...styles.customerStamp, width: width };

        return (
            <View key={"stamps"} style={{ flexDirection: "row" }} wrap={false}>
                <Text key={"stampss"} style={noteStyle}>
                    {""}
                </Text>
            </View>
        );
    };

    const createAcknowledgementSection = (width: number): JSX.Element => {
        let acknowledgement = {} as Signable;
        if (props.ticket.Signatures && props.ticket.Signatures.length > 0) {
            acknowledgement = props.ticket.Signatures[0] as Signable;
        }

        const gridRows: string[][] = [];

        if (HasValue(acknowledgement.signatory)) gridRows.push(["NAME :", acknowledgement.signatory]);
        if (HasValue(acknowledgement.signatory_job_title)) gridRows.push(["TITLE :", acknowledgement.signatory_job_title ?? ""]);
        if (HasValue(acknowledgement.signatory_email)) gridRows.push(["EMAIL ADDRESS :", acknowledgement.signatory_email ?? ""]);

        const sectionKey = "app";
        const backgroundDecorator = gridRows.length === 3 ? highlightEvenRows : allHighlights;
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "CUSTOMER ACKNOWLEDGEMENT")}
                {props.ticket.signature_pending !== 1 && createAcknowledgementSignatureBox(width, acknowledgement)}
                {props.ticket.signature_pending !== 1 &&
                    renderGrid(sectionKey + "g", width, backgroundDecorator, [30, 70], [styles.gridLabelCellRightJustified, styles.gridCell], gridRows)}
                {props.ticket.signature_pending === 1 && <Text style={styles.gridCell}>SIGNATURE PENDING</Text>}
            </View>
        );
    };

    const createAcknowledgementSignatureBox = (width: number, acknowledgement: Signable): JSX.Element => {
        return (
            <View
                key={"appsig"}
                style={{
                    padding: 2,
                    borderLeft: STANDARD_LINE,
                    borderRight: STANDARD_LINE,
                    borderBottom: STANDARD_LINE,
                    flexDirection: "row",
                    justifyContent: "center",
                }}
                wrap={false}>
                <Image style={{ height: SIGNATURE_HEIGHT, width: SIGNATURE_WIDTH }} source={acknowledgement.signature ?? transparentPixel}></Image>
            </View>
        );
    };

    const getPassengerNames = () => {
        const pax = DefinitelyAnArray(props.ticket.Pax);
        let result = "";
        let first = true;

        pax.forEach((p) => {
            if (!first) result += ", ";
            result += p.name;
            first = false;
        });

        return result;
    };

    function getCustomerRows() {
        const showSeeManifest = props.ticket.see_passenger_manifest === 1;
        const passengerCount = showSeeManifest ? "" : props.ticket.Pax?.length.toFixed(0) ?? "";
        const passengerNames = showSeeManifest ? "SEE PASSENGER MANIFEST" : getPassengerNames();

        const gridRows: string[][] = [];
        gridRows.push(["COMPANY NAME :", getCompanyName(props.ticket.company_id)]);
        gridRows.push(["JOB :", props.job?.label || ""]);
        gridRows.push(["CUSTOMER PO NO. :", props.ticket.customer_po ?? ""]);
        gridRows.push(["NO. OF PASSENGERS :", passengerCount]);
        gridRows.push(["PASSENGER NAMES :", passengerNames]);
        return gridRows;
    }

    function addToGridIfValueSet(gridRows: string[][], label: string, value: string | undefined) {
        if (HasValue(value)) {
            gridRows.push([label, value as string]);
        }
    }

    function getJobRows() {
        const gridRows: string[][] = [];

        gridRows.push(["PILOT :", getPersonName(props.ticket.pilot_person_id)]);

        const secondPilotName = HasValue(props.ticket.pilot_person_id2) ? getPersonName(props.ticket.pilot_person_id2 as number) : "";
        addToGridIfValueSet(gridRows, "SECOND PILOT :", secondPilotName);

        const ameName = HasValue(props.ticket.ame_person_id) ? getPersonName(props.ticket.ame_person_id as number) : "";
        addToGridIfValueSet(gridRows, "ENGINEER :", ameName);

        const aircraft = getAircraft(props.ticket.aircraft_id);
        gridRows.push(["AIRCRAFT REGISTRATION :", aircraft?.registration?.toLocaleUpperCase() ?? ""]);
        gridRows.push(["AIRCRAFT TYPE :", aircraft?.AircraftType?.name?.toLocaleUpperCase() ?? ""]);

        const isSubstituted = (props.ticket.is_aircraft_substituted ?? 0) !== 0;
        let substitutionText: string;
        if (isSubstituted) {
            const requestedType = getAircraft(props.ticket.substituted_aircraft_id ?? -1)?.AircraftType?.name?.toLocaleUpperCase() ?? "";
            substitutionText = `YES (${requestedType} REQUESTED)`;
        } else {
            substitutionText = "NO";
        }

        gridRows.push(["A/C SUBSTITUTED : ", substitutionText]);
        gridRows.push(["BASE LOCATION :", props.ticket.airport_code ?? ""]);
        addToGridIfValueSet(gridRows, "JOB LOCATION :", props.ticket.job_location ?? "");

        return gridRows;
    }

    function getFlightLegRows() {
        const flightLegs = DefinitelyAnArray(props.ticket.Locations?.slice());
        flightLegs.sort(sortFlightLegsByTime); // sort locations according to up_time
        const gridRows: string[][] = [];

        flightLegs.forEach((l) => {
            const row: string[] = [];

            row.push(l.location ?? "");
            row.push(l.cost_code ?? "");
            row.push(TimeCalculator.getFormatted(l.up_time));
            row.push(TimeCalculator.getFormatted(l.down_time));
            row.push(l.hours?.toFixed(1) ?? "");

            gridRows.push(row);
        });

        return gridRows;
    }

    function getFlightLegRowsWithoutTimes() {
        const flightLegs = DefinitelyAnArray(props.ticket.Locations?.slice());
        (flightLegs as TicketLocation[]).sort(sortFlightLegsManually); // sort locations according to sort_order
        const gridRows: string[][] = [];

        flightLegs.forEach((l) => {
            const row: string[] = [];

            row.push(l.location ?? "");
            row.push(l.cost_code ?? "");
            row.push(l.hours?.toFixed(1) ?? "");

            gridRows.push(row);
        });

        return gridRows;
    }

    function getChargesRows() {
        const charges = DefinitelyAnArray(props.ticket.LineItems)
            .filter((charge) => CHARGE_LINE_ITEM_TYPES.findIndex((t) => t === charge.line_item_type_id) >= 0)
            .sort((a, b) => {
                return a.line_item_type_id - b.line_item_type_id;
            });

        const gridRows: string[][] = [];

        charges.forEach((charge) => {
            const row: string[] = [];
            const label = getLineItemTypeLabel(charge.line_item_type_id);

            row.push(label);

            const precision = GetLineItemPrecision(charge.line_item_type_id);
            const quantityField = precision === 0 ? "quantity" : "amount";

            row.push(charge.comments ?? "");
            row.push(charge[quantityField]?.toFixed(precision) ?? "");

            gridRows.push(row);
        });
        return gridRows;
    }

    function getMinimumsRows() {
        const gridRows: string[][] = [];

        const minimums = DefinitelyAnArray(props.ticket.LineItems).filter((charge) => MINIMUM_LINE_ITEM_TYPES.findIndex((t) => t === charge.line_item_type_id) >= 0);

        const mimimumsApply = minimums.length > 0; // TODO need better logic

        if (mimimumsApply) {
            if (props.ticket.is_averaged) {
                gridRows.push(["DAILY MINIMUMS APPLY", ""]);
                gridRows.push([`AVERAGED OVER ${props.ticket.averaged_days ?? ""} DAYS`, ""]);
            } else {
                gridRows.push(["DAILY MINIMUMS APPLY", ""]);
            }
        } else {
            gridRows.push(["DAILY MINIMUMS DO NOT APPLY", ""]);
        }

        // TODO this should probably go in ticket validator or something
        const flightTime = TimeCalculator.getTotalFlightTime(props.ticket);
        const pilotMinimum = minimums.find((t) => t.line_item_type_id === MIN_PILOT)?.amount ?? 0;
        const helicopterMinimum = minimums.find((t) => t.line_item_type_id === MIN_HELI)?.amount ?? 0;
        let unusedMinimum = helicopterMinimum - flightTime.toNumber();
        if (unusedMinimum < 0) {
            unusedMinimum = 0;
        }

        // if (pilotMinimum > 0) {
        //     gridRows.push(["PILOT DAILY MINIMUM", pilotMinimum.toFixed(1) + " HRS"]);
        // }

        if (helicopterMinimum > 0) {
            gridRows.push(["HELICOPTER DAILY MINIMUM", helicopterMinimum.toFixed(1) + " HRS"]);
            gridRows.push(["UNUSED MINIMUM", unusedMinimum.toFixed(1) + " HRS"]);
        }
        return gridRows;
    }

    function getOperatorSuppliedFuelRows() {
        // TODO should be using the is_operator and is_customer flags on line_item_type to filter these
        const operatorSuppliedFuel = DefinitelyAnArray(props.ticket.LineItems).filter(
            (fuel) => [FUEL_BASE_OPERATOR, FUEL_REMOTE_OPERATOR].findIndex((t) => t === fuel.line_item_type_id) >= 0
        );
        return formatFuelLineItems(operatorSuppliedFuel);
    }

    function getCustomerSuppliedFuelRows() {
        const customerSuppliedFuel = DefinitelyAnArray(props.ticket.LineItems).filter((fuel) => [FUEL_REMOTE_CUSTOMER].findIndex((t) => t === fuel.line_item_type_id) >= 0);
        return formatFuelLineItems(customerSuppliedFuel);
    }

    function formatFuelLineItems(operatorSuppliedFuel: TicketLineItem[]) {
        const result: string[][] = [];

        operatorSuppliedFuel.forEach((lineItem) => {
            const row: string[] = [];

            // TODO if bailey likes this format for fuel, have Sean update the line item labels and use them instead of this ternary
            // const label = context.lineItemTypes.find((t) => t.id === lineItem.line_item_type_id)?.label ?? "";
            const label = lineItem.line_item_type_id === FUEL_BASE_OPERATOR ? "BASE" : "REMOTE";

            row.push(label);
            row.push(lineItem.comments ?? "");
            row.push(lineItem.amount?.toFixed(1) ?? "");

            result.push(row);
        });

        return result;
    }

    function getLandingsRows() {
        const landings = DefinitelyAnArray(props.ticket.LineItems).filter((fuel) => LANDING_LINE_ITEM_TYPES.findIndex((t) => t === fuel.line_item_type_id) >= 0);

        const result: string[][] = [];

        landings.forEach((lineItem) => {
            const row: string[] = [];

            row.push(lineItem.comments ?? "");
            row.push(lineItem.quantity?.toFixed(0) ?? "");

            result.push(row);
        });

        return result;
    }

    function createCustomerAndJobSection() {
        const adjustedPageWidth = PAGE_WIDTH - VERTICAL_GAP;
        const [leftWidth, rightWidth] = calculateColumnWidths(adjustedPageWidth, [50, 50]);

        const sectionKey = "cj";
        return (
            <View key={sectionKey} style={{ flexDirection: "row" }}>
                <View key={sectionKey + "2"} style={{ flexDirection: "column" }}>
                    {createJobSection(rightWidth)}
                </View>
                <View key={sectionKey + "1"} style={{ flexDirection: "column", paddingLeft: VERTICAL_GAP }}>
                    {createCustomerSection(leftWidth)}
                </View>
            </View>
        );
    }

    function createCustomerSection(width: number) {
        const columnWidths = [40, 60];
        const columnStyles = [styles.gridLabelCell, styles.gridCell];

        const gridRows = getCustomerRows();

        if (gridRows.length === 0) return <></>;

        const sectionKey = "cust";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "CUSTOMER DETAILS")}
                {renderGrid(sectionKey + "g", width, highlightOddRows, columnWidths, columnStyles, gridRows)}
            </View>
        );
    }

    function createJobSection(width: number) {
        const columnWidths = [40, 60];
        const columnStyles = [styles.gridLabelCell, styles.gridCell];

        const gridRows = getJobRows();

        if (gridRows.length === 0) return <></>;

        const sectionKey = "job";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "JOB DETAILS")}
                {renderGrid(sectionKey + "g", width, highlightOddRows, columnWidths, columnStyles, gridRows)}
            </View>
        );
    }

    const createFlightLegsSection = (): JSX.Element => {
        const columnWidths = [3.5, 1.5, 1, 1, 1];
        const columnStyles = [styles.gridCell, styles.gridCell, styles.gridCellRightJustified, styles.gridCellRightJustified, styles.gridCellRightJustified];

        const headingRow: [string[]] = [["LOCATION/LSD - NTS OR DLS", "COST CODE/AFE", "UP TIME", "DOWN TIME", "FLIGHT HRS"]];
        const headingStyles = [styles.columnHeader, styles.columnHeader, styles.columnHeaderRightJustified, styles.columnHeaderRightJustified, styles.columnHeaderRightJustified];

        const footerWidths = [7, 1];
        const footerStyles = [styles.gridLabelCellRightJustified, styles.gridCellRightJustified];
        const footerRow: string[][] = [["TOTAL FLIGHT TIME HRS", `${TimeCalculator.getTotalFlightTimeFormatted(props.ticket)}`]];

        const gridRows = getFlightLegRows();

        const sectionKey = "fl";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(PAGE_WIDTH, sectionKey, "FLIGHT LEGS")}
                {renderGrid(sectionKey + "h", PAGE_WIDTH, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", PAGE_WIDTH, highlightOddRowsAndColumnLines, columnWidths, columnStyles, gridRows)}
                {renderGrid(sectionKey + "f", PAGE_WIDTH, noHighlights, footerWidths, footerStyles, footerRow, styles.gridFooter)}
            </View>
        );
    };

    const createNoTimesFlightLegsSection = (): JSX.Element => {
        const columnWidths = [9, 3, 2];
        const columnStyles = [styles.gridCell, styles.gridCell, styles.gridCellRightJustified];

        const headingRow: [string[]] = [["LOCATION/LSD - NTS OR DLS", "COST CODE/AFE", "FLIGHT HRS"]];
        const headingStyles = [styles.columnHeader, styles.columnHeader, styles.columnHeaderRightJustified];

        const footerWidths = [8, 1, 3, 1, 4, 1];
        const footerStyles = [
            styles.gridLabelCellRightJustified,
            styles.gridCell,
            styles.gridLabelCellRightJustified,
            styles.gridCell,
            styles.gridLabelCellRightJustified,
            styles.gridCellRightJustified,
        ];
        const footerRow: string[][] = [
            [
                "FIRST UP TIME :",
                TimeCalculator.getFormatted(props.ticket.up_time),
                "LAST DOWN TIME :",
                TimeCalculator.getFormatted(props.ticket.down_time),
                "TOTAL FLIGHT TIME HRS :",
                `${TimeCalculator.getTotalFlightTimeFormatted(props.ticket)}`,
            ],
        ];

        const gridRows = getFlightLegRowsWithoutTimes();

        const sectionKey = "fl";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(PAGE_WIDTH, sectionKey, "FLIGHT LEGS")}
                {renderGrid(sectionKey + "h", PAGE_WIDTH, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", PAGE_WIDTH, highlightOddRowsAndColumnLines, columnWidths, columnStyles, gridRows)}
                {renderGrid(sectionKey + "f", PAGE_WIDTH, noHighlights, footerWidths, footerStyles, footerRow, styles.gridFooter)}
            </View>
        );
    };

    const createChargesSection = (width: number): JSX.Element => {
        const columnWidths = [40, 50, 10];
        const columnStyles = [styles.gridCell, styles.gridCell, styles.gridCellRightJustified];
        const headingRow: [string[]] = [["TYPE", "COMMENTS", "QTY"]];
        const headingStyles = [styles.columnHeader, styles.columnHeader, styles.columnHeaderRightJustified];

        const gridRows = getChargesRows();

        if (gridRows.length === 0) return <></>;

        const sectionKey = "chg";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "OTHER CHARGES")}
                {renderGrid(sectionKey + "h", width, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", width, highlightOddRowsAndColumnLines, columnWidths, columnStyles, gridRows)}
            </View>
        );
    };

    const createMinimumsSection = (width: number): JSX.Element => {
        const columnWidths = [3, 1];
        const columnStyles = [styles.gridLabelCell, styles.gridCellRightJustified];
        const gridRows = getMinimumsRows();

        const sectionKey = "min";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "DAILY MINIMUMS")}
                {renderGrid(sectionKey + "g", width, minimumsHighlighter, columnWidths, columnStyles, gridRows)}
            </View>
        );
    };

    const minimumsHighlighter = (rowIndex: number, columnIndex: number): Record<string, unknown> => {
        if (rowIndex === 0) {
            // we have data in the left column on the first row, needs to be bold
            return styles.gridCell;
        }

        return highlightOddRows(rowIndex, columnIndex);
    };

    const FUEL_COLUMN_WIDTHS = [25, 55, 20];
    const FUEL_COLUMN_STYLES = [styles.gridCell, styles.gridCell, styles.gridCellRightJustified];
    const FUEL_HEADING_STYLES = [styles.columnHeader, styles.columnHeader, styles.columnHeaderRightJustified];
    const FUEL_HEADINGS: [string[]] = [["LOCATION", "SOURCE", "LITRES"]];

    const createOperatorFuelSection = (width: number): JSX.Element => {
        const gridRows = getOperatorSuppliedFuelRows();

        if (gridRows.length === 0) return <></>;

        const sectionKey = "of";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "BAILEY SUPPLIED FUEL")}
                {renderGrid(sectionKey + "h", width, gridHeaderWithColumnLines, FUEL_COLUMN_WIDTHS, FUEL_HEADING_STYLES, FUEL_HEADINGS, styles.gridHeader)}
                {renderGrid(sectionKey + "g", width, highlightOddRows, FUEL_COLUMN_WIDTHS, FUEL_COLUMN_STYLES, gridRows)}
            </View>
        );
    };

    const createCustomerFuelSection = (width: number): JSX.Element => {
        const gridRows = getCustomerSuppliedFuelRows();

        if (gridRows.length === 0) return <></>;

        const sectionKey = "cf";
        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "CUSTOMER SUPPLIED FUEL")}
                {renderGrid(sectionKey + "h", width, gridHeaderWithColumnLines, FUEL_COLUMN_WIDTHS, FUEL_HEADING_STYLES, FUEL_HEADINGS, styles.gridHeader)}
                {renderGrid(sectionKey + "g", width, highlightOddRows, FUEL_COLUMN_WIDTHS, FUEL_COLUMN_STYLES, gridRows)}
            </View>
        );
    };

    const createLandingsSection = (width: number): JSX.Element => {
        const columnWidths = [70, 30];
        const columnStyles = [styles.gridCell, styles.gridCellRightJustified];
        const headingRow: string[][] = [["LOCATION", "LANDINGS"]];
        const headingStyles = [styles.columnHeader, styles.columnHeaderRightJustified];

        const gridRows = getLandingsRows();

        const FEE_APPLICABLE_AC_TYPES = [23]; // only the bell 212 incurs charges, TODO this is not very generic
        const aircraft = getAircraft(props.ticket.aircraft_id);
        const navCanadaFeeApplies = FEE_APPLICABLE_AC_TYPES.findIndex((t) => t === aircraft?.aircraft_type_id) >= 0;
        const navCanadaText = navCanadaFeeApplies ? "NAV CANADA FEES APPLY TO THIS A/C TYPE" : "NAV CANADA FEES DO NOT APPLY TO A/C TYPE";

        const sectionKey = "ld";

        // always want to show nav can text but don't render the grid headings if there are no landings rows
        if (gridRows.length === 0)
            return (
                <View key={sectionKey}>
                    {renderSectionHeader(width, sectionKey, "LANDING CHARGES")}
                    {createLandingsNavCanadaLine(width, sectionKey, navCanadaText)}
                </View>
            );

        return (
            <View key={sectionKey}>
                {renderSectionHeader(width, sectionKey, "LANDING CHARGES")}
                {renderGrid(sectionKey + "h", width, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", width, highlightOddRows, columnWidths, columnStyles, gridRows)}
                {createLandingsNavCanadaLine(width, sectionKey, navCanadaText)}
            </View>
        );
    };

    const createLandingsNavCanadaLine = (width: number, rootKey: string, navCanadaText: string) => {
        return (
            <View key={rootKey + "ncl"} style={{ flexDirection: "row" }}>
                <Text style={{ ...styles.gridCell, width: width }}>{navCanadaText}</Text>
            </View>
        );
    };

    function createChargesAndMinimumsSection() {
        const adjustedPageWidth = PAGE_WIDTH - VERTICAL_GAP;
        const [leftWidth, rightWidth] = calculateColumnWidths(adjustedPageWidth, [60, 40]);

        return (
            <View key={"cms"} style={{ flexDirection: "row" }}>
                <View key={"cms1"} style={{ flexDirection: "column" }}>
                    {createChargesSection(leftWidth)}
                    {createNotesSection(leftWidth)}
                    {/*{createStampSection(leftWidth)}*/}
                    {createAcknowledgementSection(leftWidth)}
                </View>
                <View key={"cms2"} style={{ flexDirection: "column", paddingLeft: VERTICAL_GAP }}>
                    {isMinimumsRequired(props.ticket.company_id) && createMinimumsSection(rightWidth)}
                    {createOperatorFuelSection(rightWidth)}
                    {createCustomerFuelSection(rightWidth)}
                    {createLandingsSection(rightWidth)}
                </View>
            </View>
        );
    }

    function createFooter() {
        const boilerplate = IsDemoMode()
            ? "This ticket has been created for demonstration purposes only and should be ignored."
            : "Invoice due 30 calendar days after flight date, interest will be charged on overdue accounts. The carriage of passengers, baggage and goods by Bailey Helicopters Ltd. is subject to the terms, conditions and limitations of liability set forth in its Tariff with CTA, an extract of which is available for examination at the office of Bailey Helicopters Ltd.";
        const sectionKey = "ft";

        const columnWidths = [1, 10, 1];
        const columnStyles = createGridColumnStyles(PAGE_WIDTH, columnWidths, [styles.footerLeft, styles.footerCenter, styles.footerRight]);

        return (
            <View key={sectionKey} style={styles.footerSection} fixed>
                <View key={sectionKey + "0"} style={columnStyles[0]}>
                    <Text>app {APPLICATION_VERSION}</Text>
                </View>
                <View key={sectionKey + "1"} style={columnStyles[1]}>
                    <Text>{boilerplate}</Text>
                </View>
                <View key={sectionKey + "2"} style={columnStyles[2]}>
                    <Text render={({ pageNumber, totalPages }) => `page ${pageNumber} / ${totalPages}`} />
                </View>
            </View>
        );
    }

    function createDemoWatermark() {
        // TODO - staging is currently set to production, need to get the environments straight and re-enable this code at some point
        // if (process.env.NODE_ENV === "production") {
        //     return <></>;
        // }
        return (
            <View style={{ position: "absolute", minWidth: "100%", minHeight: "100%", height: "100%", width: "100%" }}>
                <Text
                    style={{
                        color: "#dd33fa",
                        fontSize: 216,
                        transform: "rotate(45deg)",
                        transformOrigin: "bottom left",
                        marginLeft: 40,
                        marginTop: 60,
                    }}>
                    VOID
                </Text>
            </View>
        );
    }

    /** Renders a line of disclaimer text with transparent background and white outline (fake stroke via offset copies). */
    function renderDisclaimerLine(line: string, baseStyle: Record<string, unknown>) {
        const outlineOffset = 0.8;
        const offsets = [
            [-1, -1],
            [-1, 0],
            [-1, 1],
            [0, -1],
            [0, 1],
            [1, -1],
            [1, 0],
            [1, 1],
        ];
        return (
            <View key={line} style={{ position: "relative" }}>
                {offsets.map(([dx, dy], i) => (
                    <Text
                        key={i}
                        style={{
                            ...baseStyle,
                            color: "#FFF",
                            position: "absolute",
                            left: dx * outlineOffset,
                            top: dy * outlineOffset,
                        }}>
                        {line}
                    </Text>
                ))}
                <Text style={baseStyle}>{line}</Text>
            </View>
        );
    }

    function createDataDisclaimer() {
        const baseStyle = {
            color: "#F00",
            backgroundColor: "transparent",
            fontSize: 16,
            transform: "rotate(30deg)",
            transformOrigin: "bottom left",
            marginLeft: 80,
            padding: 6,
        };
        const lines = ["NOT A VALID DOCUMENT", "THE DATA FOR RE-GENERATING THIS TICKET IS UNAVAILABLE", "REFER TO THE ORIGINAL EMAILED PDF"];
        return (
            <View style={{ position: "absolute", left: 0, top: 0, minWidth: "100%", minHeight: "100%", height: "100%", width: "100%" }}>
                {lines.map((line, index) =>
                    renderDisclaimerLine(line, {
                        ...baseStyle,
                        marginTop: index === 0 ? 60 : 10,
                    })
                )}
            </View>
        );
    }

    const createGeneratingPdfPage = (): JSX.Element => {
        const title = "GENERATING PDF";
        const rows = [
            ["GATHERING DATA FOR THE REPORT..."],
            [""],
            [""],
            ["IF THIS PROCESS DOES NOT COMPLETE QUICKLY PLEASE REPORT THE ISSUE TO SUPPORT AT:"],
            [""],
            ["electronictickets@baileyhelicopters.com"],
        ];

        const columnWidths = [100];
        const columnStyles = [styles.gridCell];
        const headingRow: [string[]] = [[""]];
        const headingStyles = [styles.columnHeader, styles.columnHeader];

        const sectionKey = title;
        return (
            <View key={sectionKey}>
                {renderSectionHeader(PAGE_WIDTH, sectionKey, title)}
                {renderGrid(sectionKey + "h", PAGE_WIDTH, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", PAGE_WIDTH, noHighlights, columnWidths, columnStyles, rows)}
            </View>
        );
    };

    if (props.ticket === undefined) {
        return (
            <Document>
                <Page size="LETTER" style={styles.pageStyle}>
                    <Text>NO TICKET</Text>
                </Page>
            </Document>
        );
    }

    // defensive code, Chase Palmer had a weird ticket generated with no master data 2023/07/18
    if (props.aircraftList.length <= 0 || props.personList.length <= 0 || props.companyList.length <= 0 || props.lineItemTypeList.length <= 0) {
        return (
            <Document>
                <Page size="LETTER" style={styles.pageStyle}>
                    {createGeneratingPdfPage()}
                </Page>
            </Document>
        );
    }

    function createErrorsSection(messages: ValidationResult[]) {
        const errors = messages.filter((m) => m.level === ErrorLevel.Error);
        return createValidationMessages("ERRORS", errors);
    }

    function createWarningsSection(messages: ValidationResult[]) {
        const warnings = messages.filter((m) => m.level === ErrorLevel.Warn);
        return createValidationMessages("WARNINGS", warnings);
    }

    const renderValidationHeader = (width: number, rootKey: string, sectionTitle: string): JSX.Element => {
        return (
            <View key={rootKey + "sh"} style={{ flexDirection: "row" }}>
                <Text style={{ ...styles.sectionHeader, width: width, backgroundColor: "#FCC" }}>{sectionTitle}</Text>
            </View>
        );
    };

    const createValidationMessages = (title: string, messages: ValidationResult[]): JSX.Element => {
        const rows = messages.map((vr) => {
            const row = [];
            row.push(getDescriptiveName(vr.editCard) ?? "");
            row.push(vr.message ?? "");
            return row;
        });

        const columnWidths = [20, 80];
        const columnStyles = [styles.gridCell, styles.gridCell];
        const headingRow: [string[]] = [["CARD", "MESSAGE"]];
        const headingStyles = [styles.columnHeader, styles.columnHeader];

        if (rows.length === 0) return <></>;

        const sectionKey = title;
        return (
            <View key={sectionKey}>
                {renderValidationHeader(PAGE_WIDTH, sectionKey, title)}
                {renderGrid(sectionKey + "h", PAGE_WIDTH, gridHeaderWithColumnLines, columnWidths, headingStyles, headingRow, styles.gridHeader)}
                {renderGrid(sectionKey + "g", PAGE_WIDTH, highlightOddRowsAndColumnLines, columnWidths, columnStyles, rows)}
            </View>
        );
    };

    function createBreadcrumb() {
        return (
            <View style={{ position: "absolute", minWidth: "100%", minHeight: "100%", height: "100%", width: "100%", justifyContent: "flex-end" }}>
                <Text
                    style={{
                        color: "#FFF",
                        fontSize: 1,
                        marginLeft: 0,
                        marginTop: 0,
                    }}>
                    {props.breadcrumb}
                </Text>
            </View>
        );
    }

    return (
        <Document>
            <Page size="LETTER" style={styles.pageStyle}>
                {props.validationResults.length > 0 && !props.showDataDisclaimer && (
                    // 612 is letter width, height is auto when not specified
                    <View style={{ ...styles.pageStyle, backgroundColor: "#FFEEEE", marginBottom: 18 }}>
                        <Text style={{ fontSize: 24 }}>TICKET IS INCOMPLETE</Text>
                        {createErrorsSection(props.validationResults)}
                        {createWarningsSection(props.validationResults)}
                    </View>
                )}

                {IsDemoMode() && createDemoWatermark()}

                {createFormHeaderSection()}

                {createCustomerAndJobSection()}

                {UpDownTimeOnEachLeg(props.ticket) && createFlightLegsSection()}
                {!UpDownTimeOnEachLeg(props.ticket) && createNoTimesFlightLegsSection()}

                {createChargesAndMinimumsSection()}

                {createFooter()}

                {props.showDataDisclaimer && createDataDisclaimer()}

                {createBreadcrumb()}
            </Page>
        </Document>
    );
};
